const { useEffect, useRef, useState } = React;

const BASE = './';
const LOGO_V = '';

/* Background poster scroll uses a larger, varied set than the in-phone
   carousel so the strip doesn't visibly repeat the same titles close
   together. Order is intentionally mixed (in/out of TRAILERS) so the
   featured movies still appear regularly in the scroll. */
const POSTERS_BG = [
  `${BASE}marketing/posters/mandalorian-and-grogu.webp`,
  `${BASE}marketing/posters/avatar-fire.jpg`,
  `${BASE}marketing/posters/michael.webp`,
  `${BASE}marketing/posters/scream-7.jpg`,
  `${BASE}marketing/posters/dune-part-three.webp`,
  `${BASE}marketing/posters/project-hail-mary.webp`,
  `${BASE}marketing/posters/devil-wears-prada-2.webp`,
  `${BASE}marketing/posters/wuthering-heights.jpg`,
  `${BASE}marketing/posters/bone-temple.jpg`,
  `${BASE}marketing/posters/billie-eilish-tour.webp`,
  `${BASE}marketing/posters/mercy.jpg`,
  `${BASE}marketing/posters/sheep-detectives.webp`,
  `${BASE}marketing/posters/shelter.jpg`,
];

const TRAILERS = [
  { title: 'The Mandalorian and Grogu', short: 'The Mandalorian and Grogu', poster: `${BASE}marketing/posters/mandalorian-and-grogu.webp`, youtubeId: 'IHWlvwu8t1w', genre: 'SCI-FI · ADVENTURE', releaseDate: '2026-05-22' },
  { title: 'Michael',                   short: 'Michael',                   poster: `${BASE}marketing/posters/michael.webp`,                youtubeId: '3zOLzsbOleM', genre: 'MUSIC · BIOGRAPHY',  releaseDate: '2026-04-24' },
  { title: 'Dune: Part Three',          short: 'Dune: Part Three',          poster: `${BASE}marketing/posters/dune-part-three.webp`,        youtubeId: '3_9vCamtuPY', genre: 'SCI-FI · ADVENTURE', start: 4, releaseDate: '2026-12-18' },
  { title: 'The Devil Wears Prada 2',   short: 'The Devil Wears Prada 2',   poster: `${BASE}marketing/posters/devil-wears-prada-2.webp`,    youtubeId: 'e9HXmMnUEdE', genre: 'COMEDY · DRAMA',     releaseDate: '2026-05-01' },
  { title: 'Billie Eilish: Hit Me Hard and Soft — The Tour (Live in 3D)', short: 'Billie Eilish: HMHAS Tour', poster: `${BASE}marketing/posters/billie-eilish-tour.webp`, youtubeId: 'grs2hNHXKdQ', genre: 'CONCERT · MUSIC', releaseDate: '2026-02-06' },
];

const CAL_MONTHS = ['JAN','FEB','MAR','APR','MAY','JUN','JUL','AUG','SEP','OCT','NOV','DEC'];
const calMonth = (iso) => CAL_MONTHS[parseInt(iso.slice(5, 7), 10) - 1];
const calDay = (iso) => parseInt(iso.slice(8, 10), 10);

const ROTATE_MS = 7500;

/* Headline typewriter — types each segment in sequence, each on its own
   line. Plain segments are normal white, em segments are italic gold.
   Starts after the logo has had a beat to settle. */
const HERO_TITLE_SEGS = [
  { text: 'Your moviegoers saw the trailers.', em: false },
  { text: 'Now they\u2019re gone.',            em: true  },
  { text: 'That\u2019s where Movie Minder begins.', em: false },
];
const HERO_TITLE_TOTAL = HERO_TITLE_SEGS.reduce((s, seg) => s + seg.text.length, 0);
const HERO_TITLE_START_DELAY = 1500;  // logo lands first
const HERO_TITLE_SPEED = 42;          // ms per character

function HeroTitleTypewriter() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let interval;
    const start = window.setTimeout(() => {
      interval = window.setInterval(() => {
        setCount((n) => {
          if (n >= HERO_TITLE_TOTAL) {
            window.clearInterval(interval);
            return n;
          }
          return n + 1;
        });
      }, HERO_TITLE_SPEED);
    }, HERO_TITLE_START_DELAY);
    return () => {
      window.clearTimeout(start);
      if (interval) window.clearInterval(interval);
    };
  }, []);

  const done = count >= HERO_TITLE_TOTAL;

  // Which segment holds the active typing cursor right now.
  let cursor = -1;
  let acc = 0;
  for (let i = 0; i < HERO_TITLE_SEGS.length; i++) {
    acc += HERO_TITLE_SEGS[i].text.length;
    if (count < acc) { cursor = i; break; }
  }

  let used = 0;
  return (
    <h2 className={`mmh-hero-title ${done ? 'is-done' : 'is-typing'}`}>
      {HERO_TITLE_SEGS.map((seg, i) => {
        const segStart = used;
        used += seg.text.length;
        const sliceLen = Math.max(0, Math.min(count, used) - segStart);
        const cls = seg.em ? 'mmh-hero-title-em' : 'mmh-hero-title-seg';
        const showCaret = !done && i === cursor && count > 0;
        // Render every character of the segment as its own <span>. Each
        // char's position is locked from the first paint — toggling
        // `visibility:hidden` on untyped chars only changes paint, not
        // layout, so the typewriter reveal never shifts a single pixel.
        // Words are wrapped in a `.mmh-hero-title-word` (white-space:
        // nowrap) so wrap points only happen at real spaces, never
        // mid-word. The caret is inserted right before the first hidden
        // char so it tracks the typing position.
        const parts = seg.text.split(/(\s+)/);
        const children = [];
        let pos = 0;
        let caretPlaced = false;
        for (let pi = 0; pi < parts.length; pi++) {
          const part = parts[pi];
          if (!part) continue;
          if (/^\s+$/.test(part)) {
            children.push(part);
            pos += part.length;
            continue;
          }
          const wordContent = [];
          const wordStart = pos;
          for (let ci = 0; ci < part.length; ci++) {
            const absPos = wordStart + ci;
            const visible = absPos < sliceLen;
            if (!visible && !caretPlaced && showCaret) {
              wordContent.push(
                <span key={`caret-${absPos}`} className="mmh-hero-caret" aria-hidden="true" />
              );
              caretPlaced = true;
            }
            wordContent.push(
              <span
                key={ci}
                className={`mmh-hero-title-char${visible ? '' : ' is-hidden'}`}
              >
                {part[ci]}
              </span>
            );
          }
          children.push(
            <span key={`w-${pi}`} className="mmh-hero-title-word">{wordContent}</span>
          );
          pos += part.length;
        }
        if (showCaret && !caretPlaced) {
          children.push(<span key="caret-end" className="mmh-hero-caret" aria-hidden="true" />);
        }
        return <span key={i} className={cls}>{children}</span>;
      })}
    </h2>
  );
}

/* ------------------------------------------------------------------
   PurpleOrb — ONE soft blurred purple light, fixed to the viewport,
   that drifts organically inside a region tied to the currently
   active section. As you scroll, the region hands off to the next
   section and the orb glides there (with continued drift), so the
   light appears to follow you down the page.
   ------------------------------------------------------------------ */
function PurpleOrb() {
  const orbRef = React.useRef(null);
  const stateRef = React.useRef({
    // Start position: lower in the hero (below the Movie Minder box)
    // so the purple light reads as a backdrop the box sits on top of,
    // rather than rising up behind the nav.
    x: 0.50, y: 0.62,
    tx: 0.50, ty: 0.62,
    size: 0.55,
    tSize: 0.55,
    opacity: 0.95,
    tOpacity: 0.95,
    t0: performance.now(),
  });

  // Per-section anchor: where the orb wants to live, and how big,
  // expressed in viewport fractions. Keys must match real section ids.
  const ANCHORS = React.useMemo(() => ({
    top:                       { x: 0.50, y: 0.58, size: 0.60, opacity: 1.00 },  // hero (lower & roams)
    theatres:                  { x: 0.28, y: 0.60, size: 0.62, opacity: 0.80 },  // ForTheatresScene
    'circuits-proof':          { x: 0.78, y: 0.42, size: 0.50, opacity: 0.85 },  // 90-second console
    how:                       { x: 0.22, y: 0.55, size: 0.62, opacity: 0.80 },  // HowItWorksScene
    'moviegoers-experience':   { x: 0.72, y: 0.50, size: 0.46, opacity: 0.70 },  // MoviegoersHero
    value:                     { x: 0.32, y: 0.50, size: 0.44, opacity: 0.65 },  // ForMoviegoersScene
    intelligence:              { x: 0.30, y: 0.38, size: 0.46, opacity: 0.70 },  // AudienceIntelligenceScene
    privacy:                   { x: 0.72, y: 0.58, size: 0.44, opacity: 0.60 },
    scale:                     { x: 0.28, y: 0.50, size: 0.44, opacity: 0.65 },
    proof:                     { x: 0.70, y: 0.40, size: 0.46, opacity: 0.85 },  // ResultsScene
    faq:                       { x: 0.50, y: 0.70, size: 0.46, opacity: 0.58 },
    cta:                       { x: 0.50, y: 0.50, size: 0.58, opacity: 0.85 },
  }), []);

  React.useEffect(() => {
    let raf;
    const pickActive = () => {
      const ids = Object.keys(ANCHORS);
      const vh = window.innerHeight;
      let best = ids[0];
      let bestScore = -Infinity;
      for (const id of ids) {
        const el = document.getElementById(id);
        if (!el) continue;
        const r = el.getBoundingClientRect();
        // how much of this section overlaps the viewport
        const overlap = Math.max(0, Math.min(vh, r.bottom) - Math.max(0, r.top));
        // prefer the one whose center is closest to viewport center
        const center = r.top + r.height / 2;
        const distance = Math.abs(center - vh / 2);
        const score = overlap - distance * 0.3;
        if (score > bestScore) { bestScore = score; best = id; }
      }
      return best;
    };

    const update = () => {
      const s = stateRef.current;
      const now = performance.now();
      const t = (now - s.t0) / 1000;

      const activeId = pickActive();
      const anchor = ANCHORS[activeId] || ANCHORS.top;
      s.tx = anchor.x;
      s.ty = anchor.y;
      s.tSize = anchor.size;
      s.tOpacity = anchor.opacity;

      // ease current toward target (slow handoff)
      const ease = 0.035;
      s.x += (s.tx - s.x) * ease;
      s.y += (s.ty - s.y) * ease;
      s.size += (s.tSize - s.size) * ease;
      s.opacity += (s.tOpacity - s.opacity) * ease;

      // organic drift inside the current region — multi-sine layered at
      // different frequencies so the path never repeats exactly. The orb
      // is always moving, even when the user isn't scrolling, so the page
      // feels alive rather than static.
      // In the Results section and the hero we push the amplitude and
      // cadence up so the light reads as actively roaming across the
      // screen.
      const isProof = activeId === 'proof';
      const isHero  = activeId === 'top';
      const ampXY   = isProof ? 1.9 : isHero ? 2.4 : 1.0;
      const ampSize = isProof ? 1.8 : isHero ? 1.4 : 1.0;
      const rate    = isProof ? 1.6 : isHero ? 1.3 : 1.0;
      const driftX = (Math.sin(t * 0.19 * rate) * 0.065 + Math.cos(t * 0.07 * rate + 1.3) * 0.045 + Math.sin(t * 0.031 * rate) * 0.025) * ampXY;
      const driftY = (Math.cos(t * 0.15 * rate + 0.8) * 0.058 + Math.sin(t * 0.09 * rate + 2.1) * 0.04 + Math.cos(t * 0.037 * rate) * 0.022) * ampXY;
      const driftSize = 1 + (Math.sin(t * 0.13 * rate) * 0.08 + Math.cos(t * 0.21 * rate + 0.5) * 0.05) * ampSize;

      const el = orbRef.current;
      if (el) {
        const px = (s.x + driftX) * 100;
        const py = (s.y + driftY) * 100;
        const sz = s.size * driftSize * 1.18;
        el.style.left = px + '%';
        el.style.top = py + '%';
        el.style.width = (sz * 100) + 'vmin';
        el.style.height = (sz * 100) + 'vmin';
        el.style.opacity = s.opacity;
      }
      raf = requestAnimationFrame(update);
    };
    // Kick off: one synchronous update so the orb has a position even if
    // rAF is paused (e.g. hidden iframe/tab on load), then the normal rAF
    // chain takes over for continuous animation when visible.
    update();
    // If visibility changes (tab became active), make sure rAF is running.
    const onVis = () => {
      if (!document.hidden) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(update);
      }
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [ANCHORS]);

  return ReactDOM.createPortal(
    <div className="mm-orb" ref={orbRef} aria-hidden="true" />,
    document.body
  );
}

const Icon = {
  ArrowRight: (props) => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  ),
  Ticket: (props) => (
    <svg width={20} height={16} viewBox="0 0 20 16" fill="none" aria-hidden {...props}>
      <path d="M10 13C10.2833 13 10.5208 12.9042 10.7125 12.7125C10.9042 12.5208 11 12.2833 11 12C11 11.7167 10.9042 11.4792 10.7125 11.2875C10.5208 11.0958 10.2833 11 10 11C9.71667 11 9.47917 11.0958 9.2875 11.2875C9.09583 11.4792 9 11.7167 9 12C9 12.2833 9.09583 12.5208 9.2875 12.7125C9.47917 12.9042 9.71667 13 10 13ZM10 9C10.2833 9 10.5208 8.90417 10.7125 8.7125C10.9042 8.52083 11 8.28333 11 8C11 7.71667 10.9042 7.47917 10.7125 7.2875C10.5208 7.09583 10.2833 7 10 7C9.71667 7 9.47917 7.09583 9.2875 7.2875C9.09583 7.47917 9 7.71667 9 8C9 8.28333 9.09583 8.52083 9.2875 8.7125C9.47917 8.90417 9.71667 9 10 9ZM10 5C10.2833 5 10.5208 4.90417 10.7125 4.7125C10.9042 4.52083 11 4.28333 11 4C11 3.71667 10.9042 3.47917 10.7125 3.2875C10.5208 3.09583 10.2833 3 10 3C9.71667 3 9.47917 3.09583 9.2875 3.2875C9.09583 3.47917 9 3.71667 9 4C9 4.28333 9.09583 4.52083 9.2875 4.7125C9.47917 4.90417 9.71667 5 10 5ZM18 16H2C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14V10C0.55 10 1.02083 9.80417 1.4125 9.4125C1.80417 9.02083 2 8.55 2 8C2 7.45 1.80417 6.97917 1.4125 6.5875C1.02083 6.19583 0.55 6 0 6V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H18C18.55 0 19.0208 0.195833 19.4125 0.5875C19.8042 0.979167 20 1.45 20 2V6C19.45 6 18.9792 6.19583 18.5875 6.5875C18.1958 6.97917 18 7.45 18 8C18 8.55 18.1958 9.02083 18.5875 9.4125C18.9792 9.80417 19.45 10 20 10V14C20 14.55 19.8042 15.0208 19.4125 15.4125C19.0208 15.8042 18.55 16 18 16Z" fill="currentColor"/>
    </svg>
  ),
  Bell: (props) => (
    <svg width={20} height={21} viewBox="0 0 20 21" fill="none" aria-hidden {...props}>
      <path d="M2 17.05V15.05H4V8.05C4 6.66667 4.41667 5.4375 5.25 4.3625C6.08333 3.2875 7.16667 2.58333 8.5 2.25V1.55C8.5 1.13333 8.64583 0.779167 8.9375 0.4875C9.22917 0.195833 9.58333 0.05 10 0.05C10.4167 0.05 10.7708 0.195833 11.0625 0.4875C11.3542 0.779167 11.5 1.13333 11.5 1.55V2.25C12.8333 2.58333 13.9167 3.2875 14.75 4.3625C15.5833 5.4375 16 6.66667 16 8.05V15.05H18V17.05H2ZM10 20.05C9.45 20.05 8.97917 19.8542 8.5875 19.4625C8.19583 19.0708 8 18.6 8 18.05H12C12 18.6 11.8042 19.0708 11.4125 19.4625C11.0208 19.8542 10.55 20.05 10 20.05ZM0 8.05C0 6.38333 0.370833 4.85417 1.1125 3.4625C1.85417 2.07083 2.85 0.916667 4.1 0L5.275 1.6C4.275 2.33333 3.47917 3.25833 2.8875 4.375C2.29583 5.49167 2 6.71667 2 8.05H0ZM18 8.05C18 6.71667 17.7042 5.49167 17.1125 4.375C16.5208 3.25833 15.725 2.33333 14.725 1.6L15.9 0C17.15 0.916667 18.1458 2.07083 18.8875 3.4625C19.6292 4.85417 20 6.38333 20 8.05H18Z" fill="currentColor"/>
    </svg>
  ),
};

/* ------------------------------------------------------------------
   HomeHeroStage — replaces the old 3D trailer carousel.
   Background: auto-scrolling poster strip (shared with the moviegoers
   page). Foreground: phone with the trailer playing on the hero region
   and a thumbnail carousel at the bottom that auto-advances in sync
   with the currently playing trailer. CTAs sit pinned over the video.
   ------------------------------------------------------------------ */
function HomeHeroStage({ youtubeSrc }) {
  const n = TRAILERS.length;
  // Start at 2n and DECREMENT each cycle so the track slides rightward
  // (posters travel left-to-right). Seamless loop: when raw reaches n,
  // we silently jump it back up to 2n (same poster image at both
  // indices since the strip is TRAILERS × 3).
  const [raw, setRaw] = useState(2 * n);
  const [animate, setAnimate] = useState(true);
  const idx = ((raw % n) + n) % n;
  const featured = TRAILERS[idx];

  useEffect(() => {
    const id = window.setInterval(() => setRaw((r) => r - 1), ROTATE_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (raw > n) return;
    const t = window.setTimeout(() => {
      setAnimate(false);
      setRaw((r) => r + n);
    }, 480);
    return () => window.clearTimeout(t);
  }, [raw, n]);

  useEffect(() => {
    if (animate) return;
    const f = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => setAnimate(true));
    });
    return () => window.cancelAnimationFrame(f);
  }, [animate]);

  const STEP = 108; // thumb width 100 + gap 8 (see .mm-home-stage .mmh-app-thumb)
  const trackTransform = `translate3d(${STEP * (1 - raw)}px, 0, 0)`;
  const setIdx = (i) => setRaw(n + i);

  return (
    <div className="mm-home-stage">
      <div className="mm-hero-row">
        <div className="mm-hero-copy mm-hero-copy--panel">
          <div className="mm-hero-copy-text">
            <span
              className="mm-hero-copy-logo-wrap"
              style={{ '--mm-logo-mask': `url(${BASE}logo/movie-minder.svg)` }}
            >
              <img
                className="mm-hero-copy-logo"
                src={`${BASE}logo/movie-minder.svg`}
                alt="Movie Minder"
              />
              <span className="mm-hero-copy-logo-shine" aria-hidden="true" />
            </span>
            <HeroTitleTypewriter />
            <p className="mmh-hero-lead">
              As your guests leave the theater, Movie Minder reminds them of the trailers they saw, saves release dates to their calendar, and links them to your box office and exciting incentives.
            </p>
          </div>

          <div className="mm-home-stage-phone">
          <div className="mmh-phone">
          <div className="mmh-phone-frame">
            <div className="mmh-phone-notch" />
            <div className="mmh-phone-screen">
              <div className="mmh-app-status">
                <div className="mmh-app-status-l">
                  <span className="mmh-app-time">12:37</span>
                </div>
                <div className="mmh-app-status-r">
                  <span className="mmh-app-bars" />
                  <span className="mmh-app-wifi" />
                  <span className="mmh-app-batt" />
                </div>
              </div>

              <div className="mmh-app-hero" key={featured.title}>
                <div className="mm-home-stage-video">
                  <iframe
                    title={`${featured.title} trailer`}
                    src={youtubeSrc(featured.youtubeId, featured.start)}
                    frameBorder={0}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    referrerPolicy="strict-origin-when-cross-origin"
                    allowFullScreen
                  />
                </div>
                <div className="mmh-app-hero-shade" />
                <div className="mmh-app-ctas">
                  <button type="button" className="mm-cta-btn mm-cta-buy is-hero-pop">
                    <Icon.Ticket /> <span>Buy Now<span className="mm-cta-extra"> &amp; Save</span></span>
                  </button>
                  <button type="button" className="mm-cta-btn mm-cta-remind is-hero-pop">
                    <Icon.Bell /> <span>Remind Me!</span>
                  </button>
                </div>
                <div className="mm-home-cal" aria-hidden="true">
                  <div className="mm-home-cal-month">{calMonth(featured.releaseDate)}</div>
                  <div className="mm-home-cal-day">{calDay(featured.releaseDate)}</div>
                  <div className="mm-home-cal-title">{featured.short}</div>
                  <div className="mm-home-cal-confirm">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Added to calendar
                  </div>
                </div>
              </div>

              <div className="mmh-app-carousel-glow" aria-hidden="true" />
              <div className="mmh-app-carousel">
                <div
                  className="mmh-app-carousel-track"
                  style={{
                    transform: trackTransform,
                    transition: animate ? undefined : 'none',
                  }}
                >
                  {TRAILERS.concat(TRAILERS).concat(TRAILERS).map((s, i) => (
                    <div
                      key={`thumb-${i}`}
                      className={`mmh-app-thumb${i === raw ? ' is-center' : ''}`}
                    >
                      <img src={s.poster} alt="" />
                    </div>
                  ))}
                </div>
              </div>

              <div className="mmh-app-brand">
                <img
                  className="mmh-app-brand-logo"
                  src={`${BASE}logo/movie-minder.svg`}
                  alt="Movie Minder"
                />
              </div>

              <div className="mmh-app-urlbar">
                <span className="mmh-app-urlbar-back">‹</span>
                <span className="mmh-app-urlbar-url">movieminder.com</span>
                <span className="mmh-app-urlbar-more">•••</span>
              </div>
            </div>
          </div>
          <div className="mmh-phone-glow" />
          </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketingHome() {
  const heroCopyRef = useRef(null);
  const [copyRevealed, setCopyRevealed] = useState(false);

  /* Reveal the "Your moviegoers saw the trailers..." block as the user
     scrolls down to it. Each line inside cascades in via CSS transition
     delays. One-shot: once revealed we stop observing. */
  useEffect(() => {
    if (copyRevealed) return;
    const el = heroCopyRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setCopyRevealed(true);
          io.disconnect();
        }
      },
      { threshold: 0.18 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [copyRevealed]);

  useEffect(() => {
    const body = document.body;
    const prev = {
      background: body.style.background,
      color: body.style.color,
      minHeight: body.style.minHeight,
    };
    body.style.background = '#0A0A0E';
    body.style.color = '#ffffff';
    body.style.minHeight = '100vh';
    return () => {
      body.style.background = prev.background;
      body.style.color = prev.color;
      body.style.minHeight = prev.minHeight;
    };
  }, []);

  // Scroll-driven purple light on the hero-copy block: as the block scrolls
  // up through the viewport, the light travels from the top down to the
  // bottom, following the user's attention.
  useEffect(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = heroCopyRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      const total = vh + r.height;
      const traveled = vh - r.top;
      const p = Math.max(0, Math.min(1, traveled / total));
      el.style.setProperty('--mm-hero-light-y', p.toFixed(3));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const youtubeSrc = (id, start) =>
    `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&rel=0&showinfo=0&playsinline=1&iv_load_policy=3&disablekb=1${start ? `&start=${start}` : ''}`;

  return (
    <div className="mm-marketing">
      <PurpleOrb />
      {window.SharedNav ? <window.SharedNav current="home" /> : null}

      <section className="mm-hero" id="top">
        <div className="mmh-bgscroll" aria-hidden="true">
          <div className="mmh-bgscroll-strip">
            <ul className="mmh-bgscroll-track">
              {POSTERS_BG.map((src, i) => (
                <li key={`bg-a-${i}`} className="mmh-bgscroll-poster">
                  <img src={src} alt="" loading="lazy" />
                </li>
              ))}
            </ul>
            <ul className="mmh-bgscroll-track" aria-hidden="true">
              {POSTERS_BG.map((src, i) => (
                <li key={`bg-b-${i}`} className="mmh-bgscroll-poster">
                  <img src={src} alt="" loading="lazy" />
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mm-hero-inner">
          <HomeHeroStage youtubeSrc={youtubeSrc} />

          <div
            className={`mm-hero-copy mm-hero-copy--reveal${copyRevealed ? ' is-revealed' : ''}`}
            ref={heroCopyRef}
          >
            <h2 className="mm-hero-headline">
              <span className="mm-hero-line">
                <span className="gold">Movie Minder</span> makes your trailers <span className="gold">instantly transactional.</span>
              </span>
              <span className="mm-hero-line">
                No costs to circuits.
              </span>
            </h2>
            <div className="mm-hero-ctas">
              <button
                type="button"
                className="mm-btn mm-btn-primary mm-btn-lg"
                onClick={() => { window.location.href = 'contact.html'; }}
              >
                Book a demo for your theater <Icon.ArrowRight />
              </button>
            </div>
          </div>
        </div>
      </section>

      <MarketingSections />
    </div>
  );
}



/* =====================================================================
   SECTIONS — everything below the hero.
   ===================================================================== */

const SI = {
  ArrowRight: () => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
    </svg>
  ),
  Phone: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="6" y="2" width="12" height="20" rx="2.5" /><path d="M11 18h2" />
    </svg>
  ),
  Chart: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" />
    </svg>
  ),
  Shield: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M12 3 4 6v6c0 5 3.5 8.5 8 9 4.5-.5 8-4 8-9V6z" />
    </svg>
  ),
  Lock: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
    </svg>
  ),
  Server: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3" y="4" width="18" height="6" rx="1.5" /><rect x="3" y="14" width="18" height="6" rx="1.5" /><path d="M7 7h.01M7 17h.01" />
    </svg>
  ),
  Bolt: () => (
    <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M13 2 4 14h7l-1 8 9-12h-7z" />
    </svg>
  ),
  Chevron: ({ open }) => (
    <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden
      style={{ transition: 'transform 200ms var(--mm-ease-smooth)', transform: open ? 'rotate(180deg)' : 'rotate(0deg)' }}>
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
};

function HowItWorks() {
  const steps = [
    { n: '01', title: 'The invite', body: "The credits roll, the lights come up — and a QR code appears on screen while a friendly text lands on their phone. Two easy ways in. No app to download." },
    { n: '02', title: 'The replay', body: "One tap opens a simple page in under a second. The trailers they just watched. The ones they missed. Everything coming soon. Easy to scroll, easy to share." },
    { n: '03', title: 'The booking', body: "They tap a trailer, save the date, and book seats at a price they can only get from you. Every sale runs through your box office, under your brand. The revenue is yours." },
    { n: '04', title: 'The proof', body: "See exactly what's working in real time. Every ticket traces back to the screen, the showtime, and the trailer behind it. Your numbers stay yours." },
  ];
  return (
    <section className="mm-section mm-how" id="how" aria-labelledby="how-heading">
      <div className="mm-section-inner">
        <span className="mm-eyebrow">How it works</span>
        <h2 id="how-heading" className="mm-section-headline">
          From the credits to the next ticket. <span className="mm-gold-phrase">Four simple steps. No app.</span>
        </h2>
        <ol className="mm-how-grid">
          {steps.map((s) => (
            <li key={s.n} className="mm-how-step mm-card">
              <span className="mm-how-n">{s.n}</span>
              <h3 className="mm-how-title">{s.title}</h3>
              <p className="mm-how-body">{s.body}</p>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

function ForTheatres() {
  const claims = [
    { claim: 'Sell tickets while the excitement is still fresh.', proof: "Not three weeks later when the moment has passed — right now, while the soundtrack is still in their head and they want to know when the movie comes out." },
    { claim: 'Turn one-time guests into regulars.', proof: 'No apps. No accounts. No sign-up walls. Just one tap — and every ticket flows through your box office, under your brand.' },
    { claim: 'See which trailers and screens actually fill your seats.', proof: "Which theater, which showtime, which trailer led to each sale — it's all there. Schedule smarter. Book the films your audience wants." },
    { claim: 'Privacy your legal team will sign off on quickly.', proof: 'We hold only what we need — name, email, phone — and only when a guest asks for a reminder. Never sold. Never shared. Your guests stay yours.' },
  ];
  return (
    <section className="mm-section mm-theatres" id="theatres" aria-labelledby="theatres-heading">
      <div className="mm-section-inner">
        <div className="mm-theatres-body">
          <span className="mm-eyebrow">For theaters</span>
          <h2 id="theatres-heading" className="mm-section-headline">
            You worked hard to fill that seat. <span className="mm-gold-phrase">Don't let the parking lot take them back.</span>
          </h2>
          <p className="mm-section-lead">
            You put real money into making tonight great — the concessions, the staff, the projection, the whole experience. Then the credits roll, your guests walk out, and the next ticket they buy could come from anywhere. That's where Movie Minder steps in.
          </p>
          <p className="mm-section-lead mm-theatres-bridge">
            Movie Minder is the bridge from "thanks for coming" to "see you Friday." Nothing to install. No upfront cost. We only earn when you sell tickets.
          </p>
          <ul className="mm-claims">
            {claims.map((c) => (
              <li key={c.claim} className="mm-claim">
                <h3 className="mm-claim-lead">{c.claim}</h3>
                <p className="mm-claim-proof">{c.proof}</p>
              </li>
            ))}
          </ul>
          <div className="mm-econ">
            <h3 className="mm-econ-lead">The deal.</h3>
            <p className="mm-econ-body">
              Zero setup fees. Zero monthly fees. A small share on the tickets we help you sell — and nothing if we don't. That's the whole deal.
            </p>
          </div>
          <div className="mm-theatres-cta">
            <button
              type="button"
              className="mm-btn mm-btn-primary mm-btn-lg"
              onClick={() => { window.location.href = 'contact.html'; }}
            >
              Book a demo for your theater <SI.ArrowRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ForStudios() {
  const claims = [
    { claim: 'See exactly which trailers sell tickets.', proof: 'When a guest taps your trailer and books a ticket, you see it in your reporting in near real time. Real data, not estimates.' },
    { claim: 'Measure engagement screen by screen.', proof: 'Which theaters moved tickets. Which films traveled. Which trailers earned their slot — and which ones didn\u2019t. Build your release strategy on what actually happened.' },
    { claim: "A data signal you've never had before.", proof: 'A theatrical impression paired with a confirmed ticket sale. A new line in your dashboard. Yours alone.' },
    { claim: 'Your data stays your data.', proof: 'We never mix your reporting with anyone else\u2019s. The walls between studios are built in — not just promised in an NDA.' },
  ];
  return (
    <section className="mm-section mm-studios" id="studios" aria-labelledby="studios-heading">
      <div className="mm-section-inner">
        <div className="mm-studios-body">
          <span className="mm-eyebrow">For studios</span>
          <h2 id="studios-heading" className="mm-section-headline">
            The trailer plays. The ticket sells. <span className="mm-gold-phrase">Finally, you can see the line between them.</span>
          </h2>
          <p className="mm-section-lead">
            You spend millions opening a film. The trailer plays across thousands of screens. A moviegoer sees it, walks out, and buys a ticket weeks later in a different state — and nothing ties that ticket back to the trailer that sold it.
          </p>
          <p className="mm-section-lead mm-studios-bridge">
            Movie Minder closes that loop. When a trailer plays on a Movie Minder screen and the guest taps through to book, the path from trailer to ticket is direct — with your name on it.
          </p>
          <ul className="mm-claims">
            {claims.map((c) => (
              <li key={c.claim} className="mm-claim">
                <h3 className="mm-claim-lead">{c.claim}</h3>
                <p className="mm-claim-proof">{c.proof}</p>
              </li>
            ))}
          </ul>
          <div className="mm-econ">
            <h3 className="mm-econ-lead">The deal.</h3>
            <p className="mm-econ-body">
              A simple platform fee covers your reporting and integration. After that, you only pay when a guest taps your trailer or books your film. Impressions are free. Results have a price.
            </p>
          </div>
          <div className="mm-studios-cta">
            <button
              type="button"
              className="mm-btn mm-btn-primary mm-btn-lg"
              onClick={() => { window.location.href = 'contact.html'; }}
            >
              Book a demo for your studio <SI.ArrowRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function ForMoviegoers() {
  const benefits = [
    { title: 'No app. No password.', body: "Opens in their browser in under a second. Close the tab and it\u2019s gone — no clutter, no commitment." },
    { title: 'A deal they can only get from you.', body: "Advance tickets at a price they can\u2019t find anywhere else. A few dollars off, real savings, exclusive to your theater." },
    { title: 'One tap to opt in. One text to opt out.', body: "They sign up once to be reminded about a film they loved. Anytime they want it to stop, they text STOP. That\u2019s it." },
  ];
  return (
    <section className="mm-section mm-value" id="value" aria-labelledby="value-heading">
      <div className="mm-section-inner">
        <div className="mm-value-head">
          <span className="mm-eyebrow">For moviegoers</span>
          <h2 id="value-heading" className="mm-section-headline">
            The trailers they loved. <span className="mm-gold-phrase">Before they forget which was which.</span>
          </h2>
        </div>
        <div className="mm-value-body">
          <div className="mm-value-lead-wrap">
            <p className="mm-section-lead mm-value-lead">
              The lights come up. They walk to the car. By breakfast, the song they couldn&#39;t stop humming is gone, the actor they almost recognized is gone, and the release date they meant to remember is just &quot;sometime this spring.&quot;
            </p>
            <p className="mm-value-closer">
              Movie Minder catches them in that moment — between the credits and the forgetting.
            </p>
          </div>
          <ul className="mm-value-benefits">
            {benefits.map((b) => (
              <li key={b.title} className="mm-value-benefit">
                <h3 className="mm-value-benefit-title">{b.title}</h3>
                <p className="mm-value-benefit-body">{b.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

function AudienceIntelligence() {
  const stack = [
    { label: 'Clear answers', detail: 'Easy-to-read dashboards, exports, and scheduled reports.' },
    { label: 'Connections', detail: "Which trailer led to which ticket. Same visit, no guesswork." },
    { label: 'Captured activity', detail: "Just the activity tied to that showtime — nothing more." },
    { label: 'What guests do', detail: 'Taps, ticket purchases, shares, repeat visits, favorite genres.' },
  ];
  return (
    <section className="mm-section mm-intel" id="intelligence" aria-labelledby="intel-heading">
      <div className="mm-section-inner">
        <div className="mm-intel-grid">
          <div className="mm-intel-copy">
            <span className="mm-eyebrow">Audience reporting</span>
            <h2 id="intel-heading" className="mm-section-headline">
              <span className="mm-gold-phrase">Reporting built for cinema,</span> and nothing else.
            </h2>
            <p className="mm-section-lead">
              Every tap in Movie Minder tells you something useful. Which trailer played. Which screen. Which showtime. What guests did next.
            </p>
            <p className="mm-section-lead mm-intel-para-2">
              Powered by 3XM Technologies&#39; Audience Intelligence practice, our reporting turns guest activity into clear answers about your theater. You see how your screens perform. Your numbers stay yours alone.
            </p>
          </div>
          <div className="mm-intel-stack-wrap" aria-hidden>
            <ol className="mm-intel-stack">
              {stack.map((s, i) => (
                <li key={s.label} className="mm-intel-tier" style={{ '--tier-i': i }}>
                  <div className="mm-intel-tier-inner">
                    <div className="mm-intel-tier-label">{s.label}</div>
                    <div className="mm-intel-tier-detail">{s.detail}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </section>
  );
}

function Results() {
  const phases = [
    { n: '01', label: 'Build', body: 'We connect Movie Minder to your box office, put the QR code on screen, and get the text messages flowing.' },
    { n: '02', label: 'Live test', body: '60 days of real trailers on real screens, selling real tickets. Weekends and weeknights, every genre. No test data, no focus groups.' },
    { n: '03', label: 'Publish', body: 'Tap-through rates. Tickets sold. Revenue per screening. Return visits. All published, all in plain sight.' },
  ];
  return (
    <section className="mm-section mm-results" id="proof" aria-labelledby="proof-heading">
      <div className="mm-section-inner">
        <div className="mm-results-head">
          <span className="mm-eyebrow">Results</span>
          <h2 id="proof-heading" className="mm-section-headline">
            A national rollout, <span className="mm-gold-phrase">measured every step of the way.</span>
          </h2>
          <p className="mm-section-lead">
            We&#39;re not asking you to take our word for it. Movie Minder is rolling out in three clear steps — and every number from the live test will be published right here.
          </p>
        </div>
        <ol className="mm-results-phases">
          {phases.map((p) => (
            <li key={p.n} className="mm-results-phase mm-card">
              <div className="mm-results-phase-head">
                <span className="mm-results-phase-n">{p.n}</span>
                <h3 className="mm-results-phase-label">{p.label}</h3>
              </div>
              <p className="mm-results-phase-body">{p.body}</p>
            </li>
          ))}
        </ol>
        <p className="mm-results-caption">Live numbers publish after the Q3 pilot.</p>
      </div>
    </section>
  );
}

const FAQ_QUESTIONS = [
  { q: 'What does it cost to get started?', a: 'No setup fees, no monthly fees. Ever.' },
  { q: 'How quickly can we go live?', a: 'Most theaters are up and running in four to six weeks, including time to test everything end-to-end.' },
  { q: 'Will this replace our loyalty program?', a: 'Not at all. Movie Minder works right alongside it. Every ticket sells through your box office, under your brand, and your loyalty data stays yours. In fact Movie Minder helps you engage with unknown moviegoers to help you grow your loyalty program.' },
  { q: 'Who owns the guest relationship?', a: 'You do. Every ticket sells through your box office. Your guests stay your guests. We never market to them on our own.' },
  { q: 'Do we collect data from guests?', a: 'No. Your guest data is always yours.' },
  { q: 'Who sends Movie Minder messages to guests?', a: 'We create circuit branded messages for you to send to your guests, or if it makes your life easier, we can do it for you.' },
];

function FAQ() {
  const [openKey, setOpenKey] = useState(null);
  const [inView, setInView] = useState(false);
  const panelRef = React.useRef(null);

  // Observe the panel — every time it enters the viewport, retrigger
  // the stagger-in animation.
  const [inAnimRound, setInAnimRound] = useState(0);
  React.useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          setInView(true);
          setInAnimRound((n) => n + 1);
        } else {
          setInView(false);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -8% 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <section className="mm-section mm-faq" id="faq" aria-labelledby="faq-heading">
      <div className="mm-section-inner">
        <div className="mm-faq-head">
          <span className="mm-eyebrow">Frequently asked</span>
          <h2 id="faq-heading" className="mm-section-headline">
            <span className="mm-gold-phrase">FAQs</span>
          </h2>
        </div>
        <div
          ref={panelRef}
          className={`mm-faq-panel${inView ? ' is-in' : ''}`}
        >
          {FAQ_QUESTIONS.map((item, i) => {
            const key = `q-${i}`;
            const isOpen = openKey === key;
            return (
              <details
                key={`${key}-r${inAnimRound}`}
                className={`mm-faq-item${isOpen ? ' is-open' : ''}`}
                open={isOpen}
                style={{ '--mm-faq-i': i }}
                onToggle={(e) => {
                  if (e.currentTarget.open) setOpenKey(key);
                  else if (isOpen) setOpenKey(null);
                }}>
                <summary className="mm-faq-q">
                  <span>{item.q}</span>
                  <SI.Chevron open={isOpen} />
                </summary>
                <div className="mm-faq-a">{item.a}</div>
              </details>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function About() {
  return (
    <section className="mm-section mm-about" id="about" aria-labelledby="about-heading">
      <div className="mm-section-inner">
        <div className="mm-about-grid">
          <div className="mm-about-lead">
            <span className="mm-eyebrow">About</span>
            <h2 id="about-heading" className="mm-section-headline">
              A cinema product, <span className="mm-gold-phrase">built by people who love the movies.</span>
            </h2>
          </div>
          <div className="mm-about-body">
            <p className="mm-section-lead">
              Movie Minder is built by 3XM Technologies, a software team with deep experience in cinema, distribution, and audience reporting. We built Movie Minder because nothing else closes the gap between the trailer that played tonight and the ticket that sells next Friday.
            </p>
            <p className="mm-section-lead mm-about-p2">
              Our team mixes cinema veterans with software builders — projection-booth thinking meets dashboard thinking. We know what it takes to run a great theater, and we know what it takes to give you the numbers to do it even better.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* =====================================================================
   FINAL CTA GRAPHIC — three linked vignettes that mirror the headline
   "Tonight's trailers. Next weekend's tickets. Measured." and the three
   audience proofs underneath:
     1. Trailer screen   — tonight's trailers (studios proof)
     2. Ticket stub      — next weekend's tickets (theatres proof)
     3. Attribution bars — measured (circuits / dashboard proof)
   A dashed gold line threads through them so the "trailer → ticket
   → measurement" chain reads as one continuous flow.
   ===================================================================== */

function FinalCTAGraphic() {
  return (
    <div className="mm-cta-graphic" aria-hidden="true">
      <svg viewBox="0 0 520 220" xmlns="http://www.w3.org/2000/svg" role="img">
        <defs>
          <linearGradient id="cta-screen" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#2A2348" />
            <stop offset="60%"  stopColor="#1B1730" />
            <stop offset="100%" stopColor="#120E22" />
          </linearGradient>
          <linearGradient id="cta-frame" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#F2E0A6" />
            <stop offset="50%"  stopColor="#D4B86A" />
            <stop offset="100%" stopColor="#9C8344" />
          </linearGradient>
          <radialGradient id="cta-spot" cx="50%" cy="48%" r="60%">
            <stop offset="0%"   stopColor="#C4A6FF" stopOpacity="0.55" />
            <stop offset="55%"  stopColor="#7B5BCF" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#7B5BCF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="cta-ticket" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%"   stopColor="#F4E9CE" />
            <stop offset="100%" stopColor="#D4B86A" />
          </linearGradient>
          <linearGradient id="cta-ticket-shade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#000" stopOpacity="0" />
            <stop offset="100%" stopColor="#000" stopOpacity="0.18" />
          </linearGradient>
          <linearGradient id="cta-bar" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%"   stopColor="#9C8344" />
            <stop offset="100%" stopColor="#F2E0A6" />
          </linearGradient>
          {/* Soft purple ambient under each card */}
          <radialGradient id="cta-halo" cx="50%" cy="50%" r="50%">
            <stop offset="0%"   stopColor="#7B5BCF" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#7B5BCF" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Three soft halos seating each vignette in the same light */}
        <ellipse cx="92"  cy="115" rx="78" ry="64" fill="url(#cta-halo)" />
        <ellipse cx="260" cy="115" rx="78" ry="64" fill="url(#cta-halo)" />
        <ellipse cx="428" cy="115" rx="78" ry="64" fill="url(#cta-halo)" />

        {/* Connecting dashed flow line — trailer → ticket → measured.
            Sits BEHIND the three icons, with subtle gold dashes and
            small arrowheads at each handoff. */}
        <path d="M150 116 L 200 116" stroke="#E2C06B" strokeWidth="1.5"
              strokeLinecap="round" strokeDasharray="3 4" opacity="0.85" />
        <path d="M193 110 L 200 116 L 193 122" fill="none" stroke="#E2C06B"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M320 116 L 370 116" stroke="#E2C06B" strokeWidth="1.5"
              strokeLinecap="round" strokeDasharray="3 4" opacity="0.85" />
        <path d="M363 110 L 370 116 L 363 122" fill="none" stroke="#E2C06B"
              strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />

        {/* ---------- 1. TRAILER SCREEN (tonight's trailers) ---------- */}
        <g transform="translate(20 60)">
          {/* gold marquee frame */}
          <rect x="0" y="0" width="144" height="100" rx="8" fill="url(#cta-frame)" />
          <rect x="5" y="5" width="134" height="90" rx="5" fill="#0E0B1B" />
          <rect x="9" y="9" width="126" height="82" rx="3" fill="url(#cta-screen)" />
          <rect x="9" y="9" width="126" height="82" rx="3" fill="url(#cta-spot)" />

          {/* curtain swag at top of screen */}
          <path d="M9 9 Q 32 22, 55 9 Q 78 22, 101 9 Q 124 22, 135 9 L 135 11 L 9 11 Z"
                fill="#3A1418" opacity="0.5" />

          {/* play triangle */}
          <g transform="translate(72 50)">
            <circle r="17" fill="rgba(244,233,206,0.10)"
                    stroke="rgba(244,233,206,0.55)" strokeWidth="1" />
            <path d="M-5 -7 L 9 0 L -5 7 Z" fill="#F4E9CE" />
          </g>

          {/* trailer scrub bar */}
          <rect x="18" y="80" width="108" height="2.5" rx="1.25"
                fill="rgba(244,233,206,0.18)" />
          <rect x="18" y="80" width="62" height="2.5" rx="1.25" fill="#E2C06B" />
          <circle cx="80" cy="81.25" r="2.6" fill="#F4E9CE" />

          {/* marquee bulbs along top */}
          {Array.from({ length: 6 }).map((_, i) => {
            const cx = 16 + i * 22;
            return (
              <circle key={i} cx={cx} cy="3.5" r="2.3" fill="#FFE9A8"
                      opacity={0.5 + (i % 3) * 0.18}>
                <animate attributeName="opacity" values="0.4;1;0.4"
                         dur="2.6s" begin={`${(i * 0.18).toFixed(2)}s`}
                         repeatCount="indefinite" />
              </circle>
            );
          })}

          {/* caption underneath */}
          <text x="72" y="118"
                fontFamily="'Inter', system-ui, sans-serif"
                fontSize="9"
                fontWeight="500"
                letterSpacing="2"
                fill="#B5A887"
                textAnchor="middle">TONIGHT</text>
        </g>

        {/* ---------- 2. TICKET (next weekend's tickets) ---------- */}
        <g transform="translate(204 78)">
          {/* faint stack — second ticket peeking */}
          <g transform="translate(4 4)" opacity="0.45">
            <path d="M 0 8 Q 0 0, 8 0 L 100 0 Q 108 0, 108 8 L 108 14 Q 102 18, 108 22
                     L 108 30 Q 108 38, 100 38 L 8 38 Q 0 38, 0 30 L 0 22 Q 6 18, 0 14 Z"
                  fill="#9C8344" />
          </g>
          {/* primary ticket */}
          <path d="M 0 8 Q 0 0, 8 0 L 100 0 Q 108 0, 108 8 L 108 14 Q 102 18, 108 22
                   L 108 30 Q 108 38, 100 38 L 8 38 Q 0 38, 0 30 L 0 22 Q 6 18, 0 14 Z"
                fill="url(#cta-ticket)" />
          <path d="M 0 8 Q 0 0, 8 0 L 100 0 Q 108 0, 108 8 L 108 30 Q 108 38, 100 38
                   L 8 38 Q 0 38, 0 30 Z"
                fill="url(#cta-ticket-shade)" opacity="0.6" />
          <line x1="34" y1="6" x2="34" y2="32" stroke="#9C8344"
                strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
          <text x="71" y="23"
                fontFamily="Georgia, 'Times New Roman', serif"
                fontWeight="700" fontSize="7.5" letterSpacing="0.8"
                fill="#3A2A0E" textAnchor="middle">ADMIT ONE</text>
          <text x="17" y="16" fontFamily="Georgia, serif"
                fontSize="6.5" letterSpacing="0.5" fill="#3A2A0E"
                opacity="0.7" textAnchor="middle">SAT</text>
          <text x="17" y="26" fontFamily="Georgia, serif"
                fontWeight="700" fontSize="9" fill="#3A2A0E"
                textAnchor="middle">7:45</text>

          {/* caption */}
          <text x="54" y="56"
                fontFamily="'Inter', system-ui, sans-serif"
                fontSize="8"
                fontWeight="500"
                letterSpacing="1.5"
                fill="#B5A887"
                textAnchor="middle">NEXT WEEKEND</text>
        </g>

        {/* ---------- 3. ATTRIBUTION DASHBOARD (measured) ---------- */}
        <g transform="translate(372 60)">
          {/* dashboard panel */}
          <rect x="0" y="0" width="112" height="100" rx="8"
                fill="#15101F" stroke="#3B2F5C" strokeWidth="1" />
          {/* gold underline at top */}
          <rect x="10" y="10" width="22" height="2" rx="1" fill="#E2C06B" />
          {/* tiny trend label dots */}
          <circle cx="92" cy="11" r="1.6" fill="#7B5BCF" opacity="0.7" />
          <circle cx="98" cy="11" r="1.6" fill="#E2C06B" opacity="0.85" />
          <circle cx="104" cy="11" r="1.6" fill="#F4E9CE" opacity="0.6" />

          {/* baseline */}
          <line x1="10" y1="78" x2="102" y2="78"
                stroke="rgba(244,233,206,0.18)" strokeWidth="1" />

          {/* bar chart — left bars muted (baseline), right bars gold (after MM) */}
          {[
            { x: 12,  h: 14, fill: 'rgba(244,233,206,0.20)' },
            { x: 26,  h: 22, fill: 'rgba(244,233,206,0.20)' },
            { x: 40,  h: 18, fill: 'rgba(244,233,206,0.20)' },
            { x: 56,  h: 30, fill: 'url(#cta-bar)' },
            { x: 70,  h: 42, fill: 'url(#cta-bar)' },
            { x: 84,  h: 54, fill: 'url(#cta-bar)' },
          ].map((b, i) => (
            <rect key={i} x={b.x} y={78 - b.h} width="9" height={b.h}
                  rx="1.5" fill={b.fill} />
          ))}

          {/* lift arrow — diagonal across the bars */}
          <path d="M 14 70 L 96 28"
                stroke="#A78BFA" strokeWidth="1.6"
                strokeLinecap="round" fill="none" opacity="0.85" />
          <path d="M 92 24 L 100 26 L 98 34"
                fill="none" stroke="#A78BFA" strokeWidth="1.6"
                strokeLinecap="round" strokeLinejoin="round" opacity="0.85" />

          {/* "+34%" stat */}
          <text x="56" y="93"
                fontFamily="'Inter', system-ui, sans-serif"
                fontWeight="700" fontSize="10"
                letterSpacing="0.5" fill="#F4E9CE"
                textAnchor="middle">+34% lift</text>

          {/* caption */}
          <text x="56" y="118"
                fontFamily="'Inter', system-ui, sans-serif"
                fontSize="9"
                fontWeight="500"
                letterSpacing="2"
                fill="#B5A887"
                textAnchor="middle">MEASURED</text>
        </g>
      </svg>
    </div>
  );
}

function FinalCTA() {
  const promises = [
    "See it in action — a quick walkthrough on a real screen.",
    "Or test it free: 60 days, ~20 theaters, zero cost, zero risk.",
  ];
  return (
    <section className="mm-section mm-final-cta" aria-labelledby="final-cta-heading">
      <div className="mm-section-inner">
        <div className="mm-final-cta-inner">
          <FinalCTAGraphic />
          <h2 id="final-cta-heading" className="mm-final-cta-heading">
            Tonight&#39;s trailers.<br /><span className="mm-gold-phrase">Next weekend&#39;s tickets.</span>
          </h2>
          <ul className="mm-final-cta-promises">
            {promises.map((line) => (<li key={line}>{line}</li>))}
          </ul>
          <div className="mm-final-cta-actions">
            <button
              type="button"
              className="mm-btn mm-btn-primary mm-btn-lg"
              onClick={() => { window.location.href = 'contact.html'; }}
            >
              Book a demo <SI.ArrowRight />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  const year = new Date().getFullYear();
  return (
    <footer className="mm-footer" aria-label="Site footer">
      <div className="mm-section-inner mm-footer-inner">
        <div className="mm-footer-top">
          <div className="mm-footer-brand">
            <img className="mm-footer-logo" src={`${BASE}logo/movie-minder-topnav.svg`} alt="Movie Minder" />
            <p className="mm-footer-tagline">Turn today&rsquo;s trailer into<br />tomorrow&rsquo;s ticket.</p>
          </div>
          <nav className="mm-footer-nav" aria-label="Footer links">
            <div className="mm-footer-col">
              <h3 className="mm-footer-h">Pages</h3>
              <a href="index.html">Home</a>
              <a href="platform.html">Platform</a>
              <a href="about.html">About</a>
            </div>
            <div className="mm-footer-col">
              <h3 className="mm-footer-h">Explore</h3>
              <a href="#how">How it works</a>
              <a href="#proof">Results</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="mm-footer-col">
              <h3 className="mm-footer-h">Contact</h3>
              <a href="mailto:hello@3xmt.ai">hello@3xmt.ai</a>
              <a href="#">Privacy policy</a>
              <a href="#">Terms</a>
            </div>
          </nav>
        </div>
        <div className="mm-footer-bottom">
          <div className="mm-footer-legal">
            <span>&copy; {year} Movie Minder.</span>
            <span className="mm-footer-parent">A <a href="https://3xmtechnologies.com" target="_blank" rel="noopener noreferrer">3XM Technologies Inc.</a> product</span>
          </div>
          <div className="mm-footer-social" aria-label="Social links">
            <a href="#" aria-label="X">X</a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function InTheaterAdvertising() {
  const POSTER_A = `${BASE}marketing/posters/project-hail-mary.webp`;
  const POSTER_B = `${BASE}marketing/posters/dune-part-three.webp`;
  const POSTER_C = `${BASE}marketing/posters/mandalorian-and-grogu.webp`;
  const POSTER_D = `${BASE}marketing/posters/avatar-fire.jpg`;
  const POSTER_E = `${BASE}marketing/posters/wuthering-heights.jpg`;

  const SLIDES = [
    { kind: 'exterior', label: 'Exterior Posters' },
    { kind: 'digital',  label: 'In-Theater Digital Posters' },
    { kind: 'standee',  label: 'Concessions' },
  ];
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setIdx((n) => (n + 1) % SLIDES.length), 5000);
    return () => window.clearInterval(id);
  }, []);

  const QrSvg = () => (
    <svg viewBox="0 0 21 21" className="mm-itx-qr-svg" aria-hidden shapeRendering="crispEdges">
      {/* finder squares (top-left, top-right, bottom-left) */}
      <path fill="#0A0A0E" d="M0 0h7v7H0V0zm1 1v5h5V1H1zm1 1h3v3H2V2zM14 0h7v7h-7V0zm1 1v5h5V1h-5zm1 1h3v3h-3V2zM0 14h7v7H0v-7zm1 1v5h5v-5H1zm1 1h3v3H2v-3z" />
      {/* timing + data modules — pseudo-random dense pattern */}
      <path fill="#0A0A0E" d="M8 0h1v1H8zM10 0h2v1h-2zM13 0h1v1h-1zM9 1h1v1H9zM11 1h1v1h-1zM8 2h1v2H8zM10 2h1v1h-1zM12 2h1v1h-1zM13 2h1v2h-1zM10 3h2v1h-2zM9 4h1v2H9zM11 4h1v1h-1zM13 4h1v1h-1zM8 5h1v1H8zM10 5h1v1h-1zM12 5h1v1h-1zM9 6h2v1H9zM12 6h1v1h-1zM0 8h1v1H0zM2 8h1v1H2zM4 8h2v1h-2zM7 8h2v1H7zM10 8h1v1h-1zM12 8h2v1h-2zM15 8h1v1h-1zM17 8h1v1h-1zM19 8h2v1h-2zM1 9h1v1H1zM3 9h2v1H3zM6 9h1v1H6zM8 9h1v2H8zM11 9h1v1h-1zM13 9h1v1h-1zM16 9h2v1h-2zM19 9h1v1h-1zM0 10h1v1H0zM2 10h1v1H2zM5 10h1v1H5zM9 10h1v1H9zM11 10h2v1h-2zM14 10h2v1h-2zM18 10h2v1h-2zM1 11h1v1H1zM3 11h1v1H3zM6 11h1v1H6zM8 11h1v2H8zM10 11h1v1h-1zM13 11h2v1h-2zM16 11h1v1h-1zM18 11h1v1h-1zM20 11h1v1h-1zM0 12h1v1H0zM2 12h1v1H2zM5 12h1v1H5zM7 12h1v1H7zM10 12h1v1h-1zM12 12h2v1h-2zM15 12h1v1h-1zM17 12h2v1h-2zM20 12h1v1h-1zM1 13h2v1H1zM4 13h1v1H4zM6 13h1v1H6zM9 13h1v1H9zM11 13h1v1h-1zM14 13h1v1h-1zM16 13h2v1h-2zM19 13h1v1h-1zM8 14h1v2H8zM10 14h1v1h-1zM12 14h1v1h-1zM14 14h1v2h-1zM16 14h1v1h-1zM18 14h1v1h-1zM20 14h1v1h-1zM9 15h1v1H9zM11 15h2v1h-2zM15 15h1v1h-1zM17 15h2v1h-2zM10 16h1v1h-1zM13 16h1v1h-1zM16 16h1v1h-1zM18 16h1v1h-1zM20 16h1v1h-1zM8 17h2v1H8zM11 17h1v1h-1zM14 17h2v1h-2zM17 17h1v1h-1zM19 17h2v1h-2zM10 18h1v1h-1zM12 18h2v1h-2zM15 18h1v2h-1zM17 18h1v1h-1zM20 18h1v1h-1zM9 19h1v1H9zM11 19h1v1h-1zM13 19h1v2h-1zM16 19h1v1h-1zM18 19h2v1h-2zM8 20h1v1H8zM10 20h1v1h-1zM12 20h1v1h-1zM14 20h1v1h-1zM17 20h2v1h-2zM20 20h1v1h-1z" />
    </svg>
  );

  return (
    <section className="mm-itx" id="in-theater-ads" aria-labelledby="itx-heading">
      <div className="mm-itx-inner">
        <div className="mm-itx-copy">
          <span className="mm-itx-eyebrow">In‑theater advertising</span>
          <h2 id="itx-heading" className="mm-itx-headline">
            Connect with <span className="gold">unknown moviegoers.</span>
          </h2>
          <p className="mm-itx-lead">
            Use our QR code on your digital posters, standees, and other PR and marketing materials, offering Movie Minder to non-loyalty members:
          </p>
          <ol className="mm-itx-list">
            <li><span className="mm-itx-roman">I.</span><span>Increase your database of known moviegoers</span></li>
            <li><span className="mm-itx-roman">II.</span><span>Provide additional value to customers</span></li>
            <li><span className="mm-itx-roman">III.</span><span>New ways to connect, monetize and increase the value of your customer base</span></li>
          </ol>
        </div>

        <div className="mm-itx-stage" aria-hidden>
          <div className="mm-itx-slides">
            {/* 1. EXTERIOR — marquee with poster cases flanking the entrance */}
            <div className={`mm-itx-slide mm-itx-slide--exterior${idx === 0 ? ' is-active' : ''}`}>
              <div className="mm-itx-exterior">
                <div className="mm-itx-marquee">
                  <div className="mm-itx-marquee-bulbs" />
                  <div className="mm-itx-marquee-text">NOW SHOWING</div>
                </div>
                <div className="mm-itx-exterior-row">
                  <div className="mm-itx-case">
                    <img src={POSTER_A} alt="" className="mm-itx-art" />
                    <div className="mm-itx-glare" />
                    <div className="mm-itx-qr"><QrSvg /></div>
                  </div>
                  <div className="mm-itx-entrance">
                    <div className="mm-itx-entrance-door" />
                    <div className="mm-itx-entrance-door" />
                  </div>
                  <div className="mm-itx-case">
                    <img src={POSTER_B} alt="" className="mm-itx-art" />
                    <div className="mm-itx-glare" />
                    <div className="mm-itx-qr"><QrSvg /></div>
                  </div>
                </div>
                <div className="mm-itx-sidewalk" />
              </div>
              <div className="mm-itx-caption">Exterior Posters</div>
            </div>

            {/* 2. DIGITAL DISPLAYS — auditorium perspective: seats descending to the screen, posters on the side walls */}
            <div className={`mm-itx-slide mm-itx-slide--digital${idx === 1 ? ' is-active' : ''}`}>
              <div className="mm-itx-auditorium">
                {/* SVG renders ceiling/floor/walls/screen in correct perspective */}
                <svg className="mm-itx-aud-svg" viewBox="0 0 400 300" preserveAspectRatio="none" aria-hidden>
                  <defs>
                    <linearGradient id="mm-itx-floor" x1="0" y1="1" x2="0" y2="0">
                      <stop offset="0%" stopColor="#1a1024" />
                      <stop offset="100%" stopColor="#08060e" />
                    </linearGradient>
                    <linearGradient id="mm-itx-ceiling" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1a0e2a" />
                      <stop offset="100%" stopColor="#08060e" />
                    </linearGradient>
                    <linearGradient id="mm-itx-wall-l" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#08060e" />
                      <stop offset="100%" stopColor="#1a0e2a" />
                    </linearGradient>
                    <linearGradient id="mm-itx-wall-r" x1="1" y1="0" x2="0" y2="0">
                      <stop offset="0%" stopColor="#08060e" />
                      <stop offset="100%" stopColor="#1a0e2a" />
                    </linearGradient>
                    <radialGradient id="mm-itx-screen-glow" cx="0.5" cy="0.4" r="0.6">
                      <stop offset="0%" stopColor="#f0e6ff" />
                      <stop offset="55%" stopColor="#9a78d8" />
                      <stop offset="100%" stopColor="#2a1a3e" />
                    </radialGradient>
                  </defs>
                  {/* back wall */}
                  <rect x="0" y="0" width="400" height="300" fill="#050308" />
                  {/* ceiling */}
                  <polygon points="0,0 400,0 260,120 140,120" fill="url(#mm-itx-ceiling)" />
                  {/* left wall */}
                  <polygon points="0,0 140,120 140,200 0,300" fill="url(#mm-itx-wall-l)" />
                  {/* right wall */}
                  <polygon points="400,0 400,300 260,200 260,120" fill="url(#mm-itx-wall-r)" />
                  {/* floor */}
                  <polygon points="0,300 400,300 260,200 140,200" fill="url(#mm-itx-floor)" />
                  {/* distant cinema screen — bigger */}
                  <rect x="150" y="128" width="100" height="56" rx="2" fill="#0A0A0E" stroke="#1a1a22" strokeWidth="2" />
                  <rect x="154" y="132" width="92" height="48" rx="1" fill="url(#mm-itx-screen-glow)" />
                  {/* ambient screen-spill glow on the floor */}
                  <ellipse cx="200" cy="200" rx="110" ry="18" fill="rgba(154, 120, 216, 0.18)" />
                </svg>

                {/* Side wall digital posters, rotated to "lay on" the side walls */}
                <div className="mm-itx-side-digital mm-itx-side-digital--l">
                  <div className="mm-itx-digital-bezel">
                    <img src={POSTER_C} alt="" className="mm-itx-art" />
                    <div className="mm-itx-digital-scan" />
                    <div className="mm-itx-qr"><QrSvg /></div>
                  </div>
                </div>
                <div className="mm-itx-side-digital mm-itx-side-digital--r">
                  <div className="mm-itx-digital-bezel">
                    <img src={POSTER_D} alt="" className="mm-itx-art" />
                    <div className="mm-itx-digital-scan" />
                    <div className="mm-itx-qr"><QrSvg /></div>
                  </div>
                </div>

                {/* Seat rows — absolutely positioned to align with the SVG perspective floor (y_top 66.7%..100%) */}
                <div className="mm-itx-aud-rows" aria-hidden>
                  {[
                    { y: 90, w: 78, n: 8, op: 1.0  },
                    { y: 84, w: 64, n: 7, op: 0.96 },
                    { y: 79, w: 52, n: 6, op: 0.9  },
                    { y: 75, w: 42, n: 6, op: 0.82 },
                    { y: 72, w: 34, n: 5, op: 0.72 },
                    { y: 69, w: 26, n: 5, op: 0.58 },
                  ].map((row, i) => (
                    <div
                      key={i}
                      className="mm-itx-aud-row"
                      style={{
                        bottom: `${100 - row.y}%`,
                        width: `${row.w}%`,
                        opacity: row.op,
                        gridTemplateColumns: `repeat(${row.n}, 1fr)`,
                      }}
                    >
                      {Array.from({ length: row.n }).map((_, s) => (
                        <span key={s} className="mm-itx-aud-seat" />
                      ))}
                    </div>
                  ))}
                </div>
              </div>
              <div className="mm-itx-caption">In-Theater Digital Posters</div>
            </div>

            {/* 3. CONCESSIONS — popcorn box and drink, each carrying a movie poster */}
            <div className={`mm-itx-slide mm-itx-slide--standee${idx === 2 ? ' is-active' : ''}`}>
              <div className="mm-itx-lobby mm-itx-lobby--warm">
                <div className="mm-itx-concessions">
                  <div className="mm-itx-concessions-glow" />
                  <span className="mm-itx-concessions-label">CONCESSIONS</span>
                  <div className="mm-itx-menu-board">
                    <span /><span /><span />
                  </div>
                </div>

                <div className="mm-itx-concessions-row">
                  {/* Popcorn — classic red+white striped paper bucket with overflowing popcorn */}
                  <div className="mm-itx-popcorn">
                    <svg viewBox="0 0 120 18" preserveAspectRatio="none" className="mm-itx-pop-mound" aria-hidden>
                      <defs>
                        <radialGradient id="mm-pop-k" cx="0.32" cy="0.28" r="0.78">
                          <stop offset="0%" stopColor="#ffffff" />
                          <stop offset="45%" stopColor="#fdf3d8" />
                          <stop offset="100%" stopColor="#b9893c" />
                        </radialGradient>
                        <radialGradient id="mm-pop-k2" cx="0.4" cy="0.3" r="0.72">
                          <stop offset="0%" stopColor="#fff7d8" />
                          <stop offset="55%" stopColor="#f3dca0" />
                          <stop offset="100%" stopColor="#a47729" />
                        </radialGradient>
                        {/* popcorn-puff shape — gentle 5-lobe bump, mostly round with subtle undulation */}
                        <path id="mm-pop-shape" d="M -2.5 -3.4 C -2 -5.5 2 -5.5 2.5 -3.4 C 4.5 -3.2 5.2 -0.3 4 1.3 C 3.8 3.5 1.8 4.5 0 4.2 C -1.8 4.5 -3.8 3.5 -4 1.3 C -5.2 -0.3 -4.5 -3.2 -2.5 -3.4 Z" />
                      </defs>
                      {[
                        /* surface line — kernels at the fill level, lower half tucked behind the bucket rim */
                        {cx: 4,   cy: 13, sx: 0.85, sy: 0.75, r: -14, v: 1},
                        {cx: 12,  cy: 12, sx: 0.9,  sy: 0.78, r: 10},
                        {cx: 20,  cy: 13, sx: 0.9,  sy: 0.78, r: -20, v: 1},
                        {cx: 28,  cy: 12, sx: 0.95, sy: 0.82, r: 8},
                        {cx: 36,  cy: 12, sx: 0.95, sy: 0.82, r: -6,  v: 1},
                        {cx: 44,  cy: 12, sx: 1.0,  sy: 0.85, r: 14},
                        {cx: 52,  cy: 12, sx: 1.0,  sy: 0.85, r: -16, v: 1},
                        {cx: 60,  cy: 12, sx: 1.0,  sy: 0.85, r: 4,   v: 1},
                        {cx: 68,  cy: 12, sx: 1.0,  sy: 0.85, r: -10},
                        {cx: 76,  cy: 12, sx: 0.95, sy: 0.82, r: 18,  v: 1},
                        {cx: 84,  cy: 12, sx: 0.95, sy: 0.82, r: -8},
                        {cx: 92,  cy: 12, sx: 0.95, sy: 0.82, r: 22,  v: 1},
                        {cx: 100, cy: 13, sx: 0.9,  sy: 0.78, r: -14, v: 1},
                        {cx: 108, cy: 12, sx: 0.85, sy: 0.75, r: 12},
                        {cx: 116, cy: 13, sx: 0.85, sy: 0.75, r: -22, v: 1},
                        /* scoop base — shallow tier peeking just above the rim */
                        {cx: 28, cy: 10, sx: 0.95, sy: 0.82, r: -12, v: 1},
                        {cx: 40, cy:  9, sx: 1.0,  sy: 0.85, r: 8},
                        {cx: 52, cy:  9, sx: 1.0,  sy: 0.85, r: -16, v: 1},
                        {cx: 64, cy:  9, sx: 1.0,  sy: 0.85, r: 14},
                        {cx: 76, cy:  9, sx: 1.0,  sy: 0.85, r: -8,  v: 1},
                        {cx: 88, cy: 10, sx: 0.95, sy: 0.82, r: 18},
                        /* mid tier — narrower, keeps the dome convex */
                        {cx: 38, cy:  7, sx: 0.95, sy: 0.82, r: 10,  v: 1},
                        {cx: 48, cy:  6, sx: 1.0,  sy: 0.85, r: -14},
                        {cx: 58, cy:  6, sx: 1.0,  sy: 0.85, r: 6,   v: 1},
                        {cx: 68, cy:  6, sx: 1.0,  sy: 0.85, r: -18},
                        {cx: 78, cy:  7, sx: 0.95, sy: 0.82, r: 12,  v: 1},
                        /* crown — small rounded top, no sharp tip */
                        {cx: 50, cy:  4, sx: 0.95, sy: 0.82, r: -10, v: 1},
                        {cx: 60, cy:  4, sx: 1.0,  sy: 0.85, r: 14},
                        {cx: 70, cy:  4, sx: 0.95, sy: 0.82, r: -6,  v: 1},
                      ].map((k, i) => (
                        <g key={i}>
                          <use
                            href="#mm-pop-shape"
                            transform={`translate(${k.cx} ${k.cy}) rotate(${k.r || 0}) scale(${k.sx} ${k.sy})`}
                            fill={k.v ? 'url(#mm-pop-k2)' : 'url(#mm-pop-k)'}
                            stroke="rgba(150,108,48,0.55)"
                            strokeWidth="0.35"
                            vectorEffect="non-scaling-stroke"
                          />
                          {/* butter-highlight shine on larger kernels */}
                          {k.sx >= 1.0 && (
                            <ellipse
                              cx={k.cx - 1.8 * k.sx}
                              cy={k.cy - 1.8 * k.sy}
                              rx={1.4 * k.sx}
                              ry={0.9 * k.sy}
                              fill="rgba(255,255,255,0.6)"
                            />
                          )}
                        </g>
                      ))}
                    </svg>
                    <div className="mm-itx-pop-bucket">
                      <div className="mm-itx-pop-poster">
                        <img src={POSTER_A} alt="" />
                        <div className="mm-itx-pop-qr"><QrSvg /></div>
                      </div>
                    </div>
                    <svg viewBox="0 0 140 16" preserveAspectRatio="none" className="mm-itx-pop-spilled" aria-hidden>
                      <defs>
                        <path id="mm-pop-fa" d="M -2.8 -2.8 C -2.5 -5.5 2.5 -5.5 2.8 -2.8 C 5.5 -2.5 5.5 2.5 2.8 2.8 C 2.5 5.5 -2.5 5.5 -2.8 2.8 C -5.5 2.5 -5.5 -2.5 -2.8 -2.8 Z" />
                        <path id="mm-pop-fb" d="M -2.5 -3.4 C -2 -5.5 2 -5.5 2.5 -3.4 C 4.5 -3.2 5.2 -0.3 4 1.3 C 3.8 3.5 1.8 4.5 0 4.2 C -1.8 4.5 -3.8 3.5 -4 1.3 C -5.2 -0.3 -4.5 -3.2 -2.5 -3.4 Z" />
                        <path id="mm-pop-fc" d="M 0 -4.2 C 1.8 -4.5 3.8 -3.5 4 -1.3 C 5.2 0.3 4.5 3.2 2.5 3.4 C 2 5.5 -2 5.5 -2.5 3.4 C -4.5 3.2 -5.2 0.3 -4 -1.3 C -3.8 -3.5 -1.8 -4.5 0 -4.2 Z" />
                        <path id="mm-pop-fd" d="M -2 -2 C -1.7 -3.9 1.7 -3.9 2 -2 C 3.9 -1.7 3.9 1.7 2 2 C 1.7 3.9 -1.7 3.9 -2 2 C -3.9 1.7 -3.9 -1.7 -2 -2 Z" />
                      </defs>
                      {[
                        {cx: 4,   cy: 11, sx: 0.7,  sy: 0.6,  r: 10,  v: 'd'},
                        {cx: 8,   cy: 10, sx: 0.85, sy: 0.7,  r: -14, v: 'a'},
                        {cx: 15,  cy: 12, sx: 0.65, sy: 0.55, r: 18,  v: 'c'},
                        {cx: 22,  cy: 12, sx: 0.7,  sy: 0.6,  r: 22,  v: 'b'},
                        {cx: 28,  cy: 11, sx: 0.8,  sy: 0.7,  r: -8,  v: 'a'},
                        {cx: 34,  cy: 11, sx: 0.75, sy: 0.65, r: -6,  v: 'c'},
                        {cx: 42,  cy: 12, sx: 0.65, sy: 0.55, r: 14,  v: 'd'},
                        {cx: 52,  cy: 12, sx: 0.65, sy: 0.55, r: 16,  v: 'd'},
                        {cx: 64,  cy: 13, sx: 0.6,  sy: 0.5,  r: -20, v: 'd'},
                        {cx: 78,  cy: 12, sx: 0.7,  sy: 0.6,  r: 4,   v: 'c'},
                        {cx: 90,  cy: 12, sx: 0.7,  sy: 0.6,  r: -18, v: 'b'},
                        {cx: 100, cy: 11, sx: 0.75, sy: 0.65, r: 12,  v: 'a'},
                        {cx: 110, cy: 11, sx: 0.85, sy: 0.7,  r: 8,   v: 'a'},
                        {cx: 116, cy: 12, sx: 0.65, sy: 0.55, r: -22, v: 'd'},
                        {cx: 122, cy: 12, sx: 0.7,  sy: 0.6,  r: -10, v: 'c'},
                        {cx: 128, cy: 13, sx: 0.6,  sy: 0.5,  r: 6,   v: 'd'},
                        {cx: 134, cy: 11, sx: 0.6,  sy: 0.55, r: 20,  v: 'd'},
                      ].map((k, i) => (
                        <use
                          key={i}
                          href={`#mm-pop-f${k.v}`}
                          transform={`translate(${k.cx} ${k.cy}) rotate(${k.r}) scale(${k.sx} ${k.sy})`}
                          fill="url(#mm-pop-k)"
                          stroke="rgba(160,120,55,0.45)"
                          strokeWidth="0.3"
                          vectorEffect="non-scaling-stroke"
                        />
                      ))}
                    </svg>
                  </div>

                  {/* Cup — bright red with white droplets, red dome lid, red straw */}
                  <div className="mm-itx-cup">
                    <div className="mm-itx-cup-straw" />
                    <div className="mm-itx-cup-lid" />
                    <div className="mm-itx-cup-body">
                      <div className="mm-itx-cup-poster">
                        <img src={POSTER_E} alt="" />
                        <div className="mm-itx-cup-qr"><QrSvg /></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mm-itx-floor mm-itx-floor--warm">
                  <svg className="mm-itx-spilled-svg" viewBox="0 0 400 60" preserveAspectRatio="xMidYMax meet" aria-hidden>
                    <defs>
                      <radialGradient id="mm-pop-k2" cx="0.35" cy="0.28" r="0.75">
                        <stop offset="0%" stopColor="#ffffff" />
                        <stop offset="55%" stopColor="#fdf3d8" />
                        <stop offset="100%" stopColor="#c9a86a" />
                      </radialGradient>
                      <path id="mm-pop-ga" d="M -2.8 -2.8 C -2.5 -5.5 2.5 -5.5 2.8 -2.8 C 5.5 -2.5 5.5 2.5 2.8 2.8 C 2.5 5.5 -2.5 5.5 -2.8 2.8 C -5.5 2.5 -5.5 -2.5 -2.8 -2.8 Z" />
                      <path id="mm-pop-gb" d="M -2.5 -3.4 C -2 -5.5 2 -5.5 2.5 -3.4 C 4.5 -3.2 5.2 -0.3 4 1.3 C 3.8 3.5 1.8 4.5 0 4.2 C -1.8 4.5 -3.8 3.5 -4 1.3 C -5.2 -0.3 -4.5 -3.2 -2.5 -3.4 Z" />
                      <path id="mm-pop-gc" d="M 0 -4.2 C 1.8 -4.5 3.8 -3.5 4 -1.3 C 5.2 0.3 4.5 3.2 2.5 3.4 C 2 5.5 -2 5.5 -2.5 3.4 C -4.5 3.2 -5.2 0.3 -4 -1.3 C -3.8 -3.5 -1.8 -4.5 0 -4.2 Z" />
                      <path id="mm-pop-gd" d="M -2 -2 C -1.7 -3.9 1.7 -3.9 2 -2 C 3.9 -1.7 3.9 1.7 2 2 C 1.7 3.9 -1.7 3.9 -2 2 C -3.9 1.7 -3.9 -1.7 -2 -2 Z" />
                    </defs>
                    {[
                      /* scattered across the floor — no row, varied depths and gaps */
                      {cx: 18,  cy: 56, sx: 0.7,  sy: 0.6,  r: 12,  v: 'd'},
                      {cx: 42,  cy: 48, sx: 0.85, sy: 0.75, r: -22, v: 'a'},
                      {cx: 58,  cy: 55, sx: 0.75, sy: 0.65, r: 8,   v: 'd'},
                      {cx: 76,  cy: 40, sx: 0.9,  sy: 0.8,  r: 16,  v: 'c'},
                      {cx: 95,  cy: 53, sx: 1.0,  sy: 0.85, r: -10, v: 'b'},
                      {cx: 108, cy: 47, sx: 0.85, sy: 0.75, r: 20,  v: 'a'},
                      {cx: 116, cy: 57, sx: 0.7,  sy: 0.6,  r: -6,  v: 'd'},
                      {cx: 128, cy: 51, sx: 1.15, sy: 0.95, r: 18,  v: 'c'},
                      {cx: 142, cy: 44, sx: 0.95, sy: 0.85, r: -14, v: 'a'},
                      {cx: 158, cy: 56, sx: 0.8,  sy: 0.7,  r: 22,  v: 'b'},
                      {cx: 175, cy: 38, sx: 1.0,  sy: 0.9,  r: 4,   v: 'c'},
                      {cx: 192, cy: 54, sx: 0.85, sy: 0.75, r: -18, v: 'd'},
                      {cx: 210, cy: 46, sx: 0.9,  sy: 0.8,  r: 14,  v: 'a'},
                      {cx: 232, cy: 52, sx: 1.25, sy: 1.05, r: -8,  v: 'c'},
                      {cx: 248, cy: 41, sx: 0.8,  sy: 0.7,  r: 24,  v: 'b'},
                      {cx: 268, cy: 55, sx: 0.75, sy: 0.65, r: -12, v: 'd'},
                      {cx: 284, cy: 49, sx: 0.95, sy: 0.85, r: 10,  v: 'a'},
                      {cx: 302, cy: 57, sx: 0.7,  sy: 0.6,  r: -20, v: 'd'},
                      {cx: 318, cy: 43, sx: 0.9,  sy: 0.8,  r: 6,   v: 'c'},
                      {cx: 338, cy: 51, sx: 0.85, sy: 0.75, r: 18,  v: 'b'},
                      {cx: 358, cy: 57, sx: 0.75, sy: 0.65, r: -4,  v: 'd'},
                      {cx: 378, cy: 46, sx: 0.9,  sy: 0.8,  r: 14,  v: 'a'},
                    ].map((k, i) => (
                      <use
                        key={i}
                        href={`#mm-pop-g${k.v}`}
                        transform={`translate(${k.cx} ${k.cy}) rotate(${k.r}) scale(${k.sx} ${k.sy})`}
                        fill="url(#mm-pop-k2)"
                        stroke="rgba(180,140,70,0.4)"
                        strokeWidth="0.3"
                        vectorEffect="non-scaling-stroke"
                      />
                    ))}
                  </svg>
                </div>
              </div>
              <div className="mm-itx-caption">Concessions</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Benefits() {
  const items = [
    { title: 'New revenue',       desc: 'Trailer generated revenue, no additional cost to theaters.' },
    { title: 'More visits',       desc: 'Incentivize advance sales, reminders, rewards and sharing — drive repeat traffic.' },
    { title: 'Valuable insights', desc: 'Track purchase intent in real time, instantly measurable. Your data stays yours.' },
    { title: 'Wider reach',       desc: 'Engage non‑loyalty guests easily, expand first‑party loyalty membership.' },
    { title: 'Quick launch',      desc: 'Instantly interfaces with your software.' },
  ];
  const sectionRef = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0] && entries[0].isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section ref={sectionRef} className={`mm-ben${inView ? ' is-in-view' : ''}`} id="benefits" aria-labelledby="ben-heading">
      <div className="mm-ben-inner">
        <header className="mm-ben-head">
          <span className="mm-ben-eyebrow">Benefits</span>
          <h2 id="ben-heading" className="mm-ben-headline">
            Turn today’s trailer into <span className="gold">tomorrow’s ticket.</span>
          </h2>
        </header>
        <ul className="mm-ben-list">
          {items.map((b, i) => (
            <li key={b.title} className="mm-ben-item">
              <span className="mm-ben-item-num" aria-hidden>{String(i + 1).padStart(2, '0')}</span>
              <h3 className="mm-ben-item-title">{b.title}</h3>
              <p className="mm-ben-item-desc">{b.desc}</p>
            </li>
          ))}
        </ul>
        <div className="mm-ben-cta">
          <button
            type="button"
            className="mm-btn mm-btn-primary mm-btn-lg"
            onClick={() => { window.location.href = 'contact.html'; }}
          >
            Book a demo <SI.ArrowRight />
          </button>
        </div>
      </div>
    </section>
  );
}

function MarketingSections() {
  const ForTheatresScene = window.MMXForTheatres;
  const CircuitsConsoleSection = window.CircuitsConsoleSection;
  const HowItWorksScene = window.HowItWorksScene || HowItWorks;
  const MoviegoersExperience = window.MoviegoersHero;
  const ForMoviegoersScene = window.MMXForMoviegoers;
  const AudienceIntelligenceScene = window.MMXAudienceIntelligence;
  const SharedFooter = window.SharedFooter || Footer;
  return (
    <React.Fragment>
      {ForTheatresScene ? <ForTheatresScene /> : null}
      {CircuitsConsoleSection ? <CircuitsConsoleSection /> : null}
      <HowItWorksScene />
      <InTheaterAdvertising />
      <Benefits />
      {MoviegoersExperience ? <MoviegoersExperience /> : null}
      {ForMoviegoersScene ? <ForMoviegoersScene /> : null}
      {AudienceIntelligenceScene ? <AudienceIntelligenceScene /> : null}
      <FAQ />
      <FinalCTA />
      <SharedFooter />
    </React.Fragment>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<MarketingHome />);
